/**
 * Force AdSense Ad Initialization
 * 
 * Use this to manually initialize ads that are not initializing automatically
 * Run in console: forceAdInit()
 */

export const forceAdInit = () => {
  console.group('🔧 Force AdSense Ad Initialization');

  // Ensure adsbygoogle array exists
  if (typeof window === 'undefined') {
    console.error('❌ Window object not available');
    console.groupEnd();
    return;
  }

  window.adsbygoogle = window.adsbygoogle || [];
  console.log('✅ adsbygoogle array initialized');

  // Find all ad elements
  const adElements = document.querySelectorAll('.adsbygoogle');
  console.log(`Found ${adElements.length} ad element(s)`);

  if (adElements.length === 0) {
    console.warn('⚠️ No ad elements found. Make sure AdFooter, AdSidebar, or AdInArticle components are rendered.');
    console.groupEnd();
    return;
  }

  let initializedCount = 0;
  let alreadyInitializedCount = 0;
  let errorCount = 0;

  adElements.forEach((element, index) => {
    const adElement = element as HTMLElement;
    const status = adElement.getAttribute('data-adsbygoogle-status');
    const adClient = adElement.getAttribute('data-ad-client');
    const adSlot = adElement.getAttribute('data-ad-slot');

    console.group(`Ad ${index + 1}`);
    console.log(`Status: ${status || 'not initialized'}`);
    console.log(`Client: ${adClient || 'not set'}`);
    console.log(`Slot: ${adSlot || 'not set'}`);

    if (status === 'done' || status === 'filled') {
      console.log('✅ Already initialized');
      alreadyInitializedCount++;
    } else if (status === 'error') {
      console.error('❌ Ad has error status');
      errorCount++;
    } else {
      // Try to initialize
      try {
        console.log('🔄 Attempting to initialize...');
        window.adsbygoogle.push({});
        initializedCount++;
        console.log('✅ Push successful - AdSense should process this ad');
        
        // Check status after a short delay
        setTimeout(() => {
          const newStatus = adElement.getAttribute('data-adsbygoogle-status');
          console.log(`Status after initialization: ${newStatus || 'still not initialized'}`);
          
          if (newStatus === 'done' || newStatus === 'filled') {
            console.log('✅ Ad successfully initialized!');
          } else if (newStatus === 'error') {
            console.error('❌ Ad initialization failed');
          } else {
            console.warn('⚠️ Ad still not initialized. Possible causes:');
            console.warn('   - AdSense account not approved');
            console.warn('   - Site not approved in AdSense Dashboard');
            console.warn('   - Invalid Slot ID');
            console.warn('   - Ad blocker interfering');
          }
        }, 2000);
      } catch (error: any) {
        console.error('❌ Error initializing ad:', error);
        errorCount++;
      }
    }
    console.groupEnd();
  });

  console.group('📊 Summary');
  console.log(`✅ Initialized: ${initializedCount}`);
  console.log(`✅ Already initialized: ${alreadyInitializedCount}`);
  console.log(`❌ Errors: ${errorCount}`);
  console.groupEnd();

  if (initializedCount > 0) {
    console.log('💡 Wait a few seconds and check the ad status again.');
    console.log('💡 If ads still don\'t show, check:');
    console.log('   1. AdSense account approval status');
    console.log('   2. Site approval in AdSense Dashboard');
    console.log('   3. Slot IDs are correct');
    console.log('   4. No ad blockers active');
  }

  console.groupEnd();

  return {
    total: adElements.length,
    initialized: initializedCount,
    alreadyInitialized: alreadyInitializedCount,
    errors: errorCount
  };
};

// Make it available globally
if (typeof window !== 'undefined') {
  (window as any).forceAdInit = forceAdInit;
}

